import { useState } from 'react';

import { Input } from '@toolbox/components';
import { useField } from '@toolbox/form';

import type { FieldPath, FieldValues, UseFieldProps } from '@toolbox/form';
import type { InputProps } from '@toolbox/components';
import type { ChangeEvent } from 'react';
import type { FieldPathValue, UnpackNestedValue } from 'react-hook-form/dist/types';

export type TextInputFieldProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = Omit<InputProps, 'error' | 'onChange' | 'name' | 'defaultValue'> &
  UseFieldProps<TFieldValues, TName> & {
    defaultErrorMessage?: string;
    showTooltip?: boolean;
    tooltip?: string;
    format?: (
      value: UnpackNestedValue<FieldPathValue<TFieldValues, TName>>,
    ) => UnpackNestedValue<FieldPathValue<TFieldValues, TName>>;
  };

export const TextInputField = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  name,
  defaultErrorMessage = 'Ihre Eingabe ist nicht korrekt',
  keepErrorSpace = true,
  defaultValue,
  required,
  rules,
  showTooltip,
  tooltip,
  tooltipPlacement,
  format = (_) => _ as never,
  control,
  ...props
}: TextInputFieldProps<TFieldValues, TName>): JSX.Element => {
  const { field, fieldState } = useField<TFieldValues, TName>({
    name,
    defaultValue,
    rules: {
      required,
      ...rules,
    },
    control,
  });

  const [value, setValue] = useState<string>(field.value ?? '');

  const handleChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const trimmedValue = (event.target.value || '').trim();

    setValue(event.target.value || '');

    const formattedValue = format(
      (trimmedValue || null) as UnpackNestedValue<FieldPathValue<TFieldValues, TName>> as any,
    );

    field.onChange(formattedValue);
  };

  // const handleChange = (event: ChangeEvent<HTMLInputElement>): void => {
  //   field.onChange(
  //     format(
  //       (event.target.value || null) as UnpackNestedValue<FieldPathValue<TFieldValues, TName>>,
  //     ),
  //   );
  // };

  // const handleBlur = (event: ChangeEvent<HTMLInputElement>): void => {
  //   field.onChange(event.target.value.trim());
  //
  //   console.log('BLUR', field);
  // };

  return (
    <Input
      {...props}
      {...field}
      showTooltip={showTooltip}
      tooltip={tooltip}
      keepErrorSpace={keepErrorSpace}
      error={fieldState.error?.message || (fieldState.invalid && defaultErrorMessage)}
      value={value}
      tooltipPlacement={tooltipPlacement}
      onChange={handleChange}
    />
  );
};
